/**
 * The floating tile on the native call stage (VRSLayout.native.tsx) and the
 * web call stage (useSelfViewPosition.web.ts): your own video.
 *
 * Robert, 2026-10-05, about the self view: it "SHOULD be able to stay
 * anywhere on the screen".
 *
 * Robert, 2026-10-08: the see-through self view (60% solid at rest, PR #134)
 * looked "blurry/fuzzy", because the video under it showed through. He chose
 * a solid tile: drag it away to see the hands under it.
 *
 * Robert, 2026-10-06: it sat "way above the edge/corner of the phone" and
 * "too far away from the right-hand (or left-hand) edge"; it "should move
 * above the toolbar when the toolbar is showing", not all the time; and when
 * the phone turns it "should be anchored by default to a specific corner".
 *
 * - The tile starts in the bottom-right corner: TILE_EDGE_GAP from the side
 *   of the screen and from the bottom of the safe area (the home indicator).
 * - The sides of the safe area are not used: in landscape they are as wide as
 *   the notch on both sides, which put the tile far from the edge.
 * - While the toolbar shows, a tile that it would cover moves up just clear
 *   of it (toolbarLift), and goes back down when the toolbar hides.
 * - It moves anywhere, and there is no snap to a side: it stays where it is
 *   dropped.
 * - When the phone turns, it goes to the corner it was nearest to
 *   (nearestCorner, cornerPoint). Other size changes (the camera's shape, the
 *   chat) keep its place as a share of the free space (carryTile).
 *
 * Pure helpers here; the component owns the gesture and the animation.
 */
/**
 * The space between the tile and the side of the screen, the top and bottom
 * of the safe area, and the top of the toolbar.
 */
export const TILE_EDGE_GAP = 8;
/**
 * Where the tile's top-left corner can go: TILE_EDGE_GAP in from the sides of
 * the screen, and from the top and bottom of the safe area, less the size of
 * the tile.
 *
 * @param {ITileSize} stage - The stage (the whole call screen).
 * @param {IEdgeInsets} insets - The safe area. Only the top and the bottom
 * are used.
 * @param {ITileSize} tile - The size of the tile.
 * @returns {ITileBounds}
 */
export function floatingTileBounds(stage, insets, tile) {
    const minX = TILE_EDGE_GAP;
    const minY = insets.top + TILE_EDGE_GAP;
    return {
        maxX: Math.max(minX, stage.width - TILE_EDGE_GAP - tile.width),
        maxY: Math.max(minY, stage.height - insets.bottom - TILE_EDGE_GAP - tile.height),
        minX,
        minY
    };
}
/**
 * The nearest point inside the bounds.
 *
 * @param {ITilePoint} point - Where the tile is, or where it was dropped.
 * @param {ITileBounds} bounds - Where it can go.
 * @returns {ITilePoint}
 */
export function clampTile(point, bounds) {
    return {
        x: Math.min(bounds.maxX, Math.max(bounds.minX, point.x)),
        y: Math.min(bounds.maxY, Math.max(bounds.minY, point.y))
    };
}
/**
 * The place of the tile in a corner of the bounds.
 *
 * @param {TileCorner} corner - The corner.
 * @param {ITileBounds} bounds - Where the tile can go.
 * @returns {ITilePoint}
 */
export function cornerPoint(corner, bounds) {
    return {
        x: corner.endsWith('left') ? bounds.minX : bounds.maxX,
        y: corner.startsWith('top') ? bounds.minY : bounds.maxY
    };
}
/**
 * The first place of the tile: the bottom-right corner. While the toolbar
 * shows, the component lifts it clear of the toolbar (toolbarLift).
 *
 * @param {ITileBounds} bounds - Where the tile can go.
 * @returns {ITilePoint}
 */
export function firstTilePosition(bounds) {
    return cornerPoint('bottom-right', bounds);
}
/**
 * The corner nearest to the tile: the half of the free space it is in,
 * across and down. A tile exactly in the middle goes right and down, to the
 * first corner.
 *
 * @param {ITilePoint} point - Where the tile is.
 * @param {ITileBounds} bounds - Where it can go.
 * @returns {TileCorner}
 */
export function nearestCorner(point, bounds) {
    const top = point.y < (bounds.minY + bounds.maxY) / 2;
    const left = point.x < (bounds.minX + bounds.maxX) / 2;
    return `${top ? 'top' : 'bottom'}-${left ? 'left' : 'right'}`;
}
/**
 * How far the tile moves up while the toolbar shows: just enough that its
 * bottom is TILE_EDGE_GAP above the top of the toolbar. Zero for a tile that
 * the toolbar does not cover. Never past the top of its bounds.
 *
 * @param {ITilePoint} point - Where the tile rests.
 * @param {number} tileHeight - The height of the tile.
 * @param {number} toolbarTop - The top of the toolbar on the stage.
 * @param {number} minY - The top of the tile's bounds.
 * @returns {number} The distance up, in points (0 or more).
 */
export function toolbarLift(point, tileHeight, toolbarTop, minY) {
    const overlap = point.y + tileHeight - (toolbarTop - TILE_EDGE_GAP);
    return Math.max(0, Math.min(overlap, point.y - minY));
}
/**
 * The share of the free space before a point (0 at the start, 1 at the end).
 *
 * @param {number} value - The point.
 * @param {number} min - The start of the free space.
 * @param {number} max - The end of the free space.
 * @returns {number}
 */
function shareOf(value, min, max) {
    return max > min ? (value - min) / (max - min) : 1;
}
/**
 * Where the tile goes when its bounds change and the phone did not turn (the
 * camera's shape, the chat): the same share of the free space across and
 * down. Clamped to the new bounds.
 *
 * @param {ITilePoint} point - Where the tile is.
 * @param {ITileBounds} from - The old bounds.
 * @param {ITileBounds} to - The new bounds.
 * @returns {ITilePoint}
 */
export function carryTile(point, from, to) {
    if (from.minX === to.minX && from.maxX === to.maxX && from.minY === to.minY && from.maxY === to.maxY) {
        return clampTile(point, to);
    }
    return clampTile({
        x: to.minX + (shareOf(point.x, from.minX, from.maxX) * (to.maxX - to.minX)),
        y: to.minY + (shareOf(point.y, from.minY, from.maxY) * (to.maxY - to.minY))
    }, to);
}
/**
 * Where the tile goes when the phone turns: the corner it was nearest to.
 *
 * @param {ITilePoint} point - Where the tile was.
 * @param {ITileBounds} from - The bounds before the turn.
 * @param {ITileBounds} to - The bounds after the turn.
 * @returns {ITilePoint}
 */
export function turnTile(point, from, to) {
    return cornerPoint(nearestCorner(point, from), to);
}
